import React, { useEffect, useRef, useState } from 'react';

const Badge = ({ imageUrl, alt, description, locked }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const badgeRef = useRef(null);

  const getBadgeColor = (text) => {
    const colors = [
      'from-yellow-100 to-yellow-200 border-yellow-300',
      'from-blue-100 to-blue-200 border-blue-300',
      'from-green-100 to-green-200 border-green-300',
      'from-purple-100 to-purple-200 border-purple-300',
      'from-pink-100 to-pink-200 border-pink-300',
      'from-orange-100 to-orange-200 border-orange-300',
      'from-teal-100 to-teal-200 border-teal-300',
      'from-red-100 to-red-200 border-red-300',
    ];
    const index = text.length % colors.length;
    return colors[index];
  };

  const badgeColor = locked ? 'from-gray-100 to-gray-200 border-gray-300' : getBadgeColor(alt);

  // Calculate tooltip position when showing
  const calculateTooltipPosition = () => {
    if (!badgeRef.current) return;

    const badgeRect = badgeRef.current.getBoundingClientRect();
    const tooltipWidth = 192; // w-48 = 192px
    const tooltipHeight = 120; // Approximate height

    // Default to bottom-center of the badge
    let top = badgeRect.bottom + window.scrollY + 8;
    let left = badgeRect.left + window.scrollX + badgeRect.width / 2 - tooltipWidth / 2;

    // Check if tooltip would go off screen and adjust
    if (left < 10) {
      left = 10; // Minimum margin from left edge
    } else if (left + tooltipWidth > window.innerWidth - 10) {
      left = window.innerWidth - tooltipWidth - 10; // Minimum margin from right edge
    }

    // If tooltip would go below viewport, show it above the badge instead
    if (top + tooltipHeight > window.innerHeight + window.scrollY - 10) {
      top = badgeRect.top + window.scrollY - tooltipHeight - 8;
    }

    setTooltipPosition({ top, left });
  };

  const handleMouseEnter = () => {
    setShowTooltip(true);
    calculateTooltipPosition();
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  // Recalculate position on scroll or resize
  useEffect(() => {
    if (showTooltip) {
      const handleScrollOrResize = () => {
        calculateTooltipPosition();
      };

      window.addEventListener('scroll', handleScrollOrResize, true);
      window.addEventListener('resize', handleScrollOrResize);

      return () => {
        window.removeEventListener('scroll', handleScrollOrResize, true);
        window.removeEventListener('resize', handleScrollOrResize);
      };
    }
  }, [showTooltip]);

  return (
    <div className="relative group" ref={badgeRef}>
      <div
        className="relative w-20 h-20 cursor-pointer transform transition-all duration-200 hover:scale-110 hover:z-10"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div
          className={`absolute inset-0 rounded-[20%] bg-linear-to-br ${badgeColor} p-[2px] shadow-lg ${locked ? 'opacity-75' : ''}`}
        >
          <div className="w-full h-full rounded-[20%] bg-white p-[2px]">
            <div
              className={`w-full h-full rounded-[20%] bg-linear-to-br ${badgeColor} p-[2px] flex items-center justify-center relative`}
            >
              <img
                src={imageUrl}
                alt={alt}
                className={`w-[90%] h-[90%] rounded-[20%] object-contain transform transition-all duration-200 
                                    ${locked ? 'grayscale opacity-50' : ''}`}
              />
              {locked && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-10 w-10 text-gray-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                  </svg>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showTooltip && (
        <div
          className="fixed z-100 w-48 p-4 bg-white rounded-lg shadow-xl pointer-events-none border border-gray-200"
          style={{
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`,
          }}
        >
          <div className="relative">
            <div className="font-bold mb-2 text-gray-800">{alt}</div>
            <div className="text-gray-600 text-sm">
              {locked ? '🔒 ' : '🏆 '}
              {description}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Badge;
